<template>
    <vs-row>
        <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="tooltip-actions mb-3">
            <label class="vs-input--label mb-1">{{ label }}</label>
            <template v-if="!disabled">
                <template v-if="!disable_current_location">
                    <vs-button @click.prevent="clear_watch_id" type="filled" color="primary" size="small" class="px-2 py-1 mx-2">
                        Lock
                    </vs-button>
                    <small>Accuracy: {{ accuracy ? accuracy.toFixed(3) : '' }} m</small>
                </template>
                <template v-else>
                    <vx-tooltip title="Enable current location" color="primary" v-if="!disabled">
                        <vs-button @click.prevent="load_current_location" type="flat" color="primary" size="full" class="px-2 py-1" :disabled="loading">
                            <font-awesome-icon :icon="mapIcon"/>
                        </vs-button>
                    </vx-tooltip>
                </template>
            </template>
        </vs-col>
        <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-2">
            <div class="flex" style="align-items: center">
                <label class="vs-input--label mr-2">Lat:</label>
                <vs-input
                    v-model="data.latitude"
                    class="w-full"
                    :disabled="disabled"
                />
            </div>
            <small class="block text-danger" v-if="api_errors.latitude">
                {{ api_errors.latitude.join(', ') }}
            </small>
        </vs-col>
        <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-2">
            <div class="flex" style="align-items: center">
                <label class="vs-input--label mr-2">Long:</label>
                <vs-input
                    v-model="data.longitude"
                    class="w-full"
                    :disabled="disabled"
                />
            </div>
            <small class="block text-danger" v-if="api_errors.longitude">
                {{ api_errors.latitude.join(', ') }}
            </small>
        </vs-col>
        <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="mt-3" v-if="$store.state.is_online">
            <div ref="map" class="map"></div>
            <small class="block mt-2" v-if="!disabled">Click anywhere on map to select</small>
        </vs-col>
        <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="mt-3" v-else>
            <small class="block mt-2" v-if="!disabled">Map has been disabled in offline mode.</small>
        </vs-col>
    </vs-row>
</template>

<script>
import { faCrosshairs, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Loader } from "@googlemaps/js-api-loader"

export default {
    props: {
        latitude: {
            required: true,
            default: null
        },
        longitude: {
            required: true,
            default: null
        },
        api_errors: {
            required: true,
            type: Object
        },
        disabled: {
            required: false,
            type: Boolean,
            default: false
        },
        label: {
            required: false,
            type: String,
            default: "Enter Location (Enter coordinates, or select on Map)"
        }
    },
    data() {
        return {
            key: 'AIzaSyA4YZBHBGWNlrV-ONhhJbe8_ep0gmP1DkM',
            map_loaded: false,

            loading: false,

            data: {
                latitude: this.latitude,
                longitude: this.longitude
            },

            map_view: {
                active: false,
                latitude: null,
                longitude: null
            },

            watch_id: null,
            accuracy: null,
            disable_current_location: false
        }
    },
    watch: {
        latitude(newV, oldV) {
            if(newV == oldV) return
            this.data.latitude = newV
        },
        longitude(newV, oldV) {
            if(newV == oldV) return
            this.data.longitude = newV
        },
        data: {
            deep: true,
            handler: function (newV) {
                this.update_marker()
                this.show_marker(this.data.latitude, this.data.longitude)
                this.$emit('update:latitude', this.data.latitude)
                this.$emit('update:longitude', this.data.longitude)
            }
        }
    },
    mounted() {
        if (this.$store.state.is_online) {
            this.load_maps().then(
                () => this.update_marker()
            )
        } else {
            if (!this.data.latitude || !this.data.longitude) {
                this.load_current_location(false)
            }
        }
    },
    beforeDestroy() {
        this.clear_watch_id()
    },
    computed: {
        mapIcon: () => faCrosshairs,
        spinIcon: () => faSpinner
    },
    methods: {
        load_maps() {
            const loader = new Loader({
                apiKey: this.key,
                version: "weekly"
            });
            
            return new Promise( (resolve, reject) => {
                loader.load().then(() => {
                    let map_options = {
                        zoom: 8,
                        controlSize: 25,
                        fullscreenControl: false,
                        streetViewControl: false
                    }
                    
                    map_options['center'] = this.get_current_userdefined_coords()
    
                    this.map = new google.maps.Map(this.$refs['map'], map_options);
                    
                    if (!this.data.latitude || !this.data.longitude)
                        this.load_current_location()
                    
                    if (this.data.latitude && this.data.longitude) {
                        this.update_marker()
                    } else {
                        this.show_marker(map_options['center']['lat'], map_options['center']['lng'])
                    }
                    
                    if (!this.disabled) {
                        this.map.addListener('click', (event) => this.show_marker(event.latLng.lat(), event.latLng.lng()))
                    }
                    this.map_loaded = true
                    resolve()
                });
            })
        },
        update_marker(){
            console.log('updating marker')
            if (this.data.latitude && this.data.longitude) this.show_marker(this.data.latitude, this.data.longitude, false)
        },
        show_marker(lat, lng, update_values=true) {
            if (!this.map) return
            if (!lat || !lng) return
            
            lat = Number(lat)
            lng = Number(lng)
            if (!this.marker) {
                this.marker = new google.maps.Marker({
                    position: {lat: lat, lng: lng},
                    map: this.map,
                    title: "Selected Location",
                    draggable: !this.disabled
                });
                let $this = this
                if (!this.disabled)
                    this.marker.addListener('dragend', function(event) {
                        let new_position = $this.marker.getPosition()
                        $this.data.latitude = new_position.lat()
                        $this.data.longitude = new_position.lng()
                        $this.clear_watch_id()
                    })
            } else {
                this.marker.setPosition({lat, lng})
            }
            this.map.setCenter({lat, lng})
            if (update_values && !this.disabled) {
                this.data.latitude = lat
                this.data.longitude = lng
            }
        },
        get_current_userdefined_coords() {
            if (!this.data.latitude || !this.data.longitude)
                return {lat: 28.6139, lng: 77.2090}

            return { lat: Number(this.data.latitude), lng: Number(this.data.longitude) }
        },
        load_current_location(update_marker=true) {
            if (this.disabled) return 

            this.disable_current_location = false

            this.watch_id = navigator.geolocation.watchPosition(
                res => {
                    if (this.accuracy && res.coords.accuracy > this.accuracy) {
                        console.log('greater')
                    } else {
                        if (!this.disabled) {
                            if (update_marker) this.show_marker(res.coords.latitude, res.coords.longitude)
                            else {
                                this.data.latitude = res.coords.latitude
                                this.data.longitude = res.coords.longitude
                            }
                        }
                    }
                    this.accuracy = res.coords.accuracy
                },
                err => {
                    // if user is online then only do the stuff 
                    if(this.$store.state.is_online) {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error Occured',
                            text: "Enable to detect current location.",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                }
            );
        },
        clear_watch_id() {
            if (this.watch_id) {
                navigator.geolocation.clearWatch(this.watch_id);
            }
            this.disable_current_location = true
            this.accuracy = null
        }
    }
}
</script>

<style lang="scss">
    .map {
        height: 250px;
    };

    .tooltip-actions {
      .con-vs-tooltip {
        display: inline-flex;
      }
    }
    
</style>